import React, { useMemo, useState } from 'react';
import { DataTable } from '@components/organisms/Documents/components/DataTable';
import { Button } from '@components/atoms/Button';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { BudgetLineItem } from '@models/lineItem.model';
import { LineItemFormModal } from '@components/organisms/LineItemFormModal';
import { useCreateLineItem } from '@hooks/api/lineItem/useCreateLineItem';
import { useUpdateLineItem } from '@hooks/api/lineItem/useUpdateLineItem';
import { useDeleteLineItem } from '@hooks/api/lineItem/useDeleteLineItem';
import { Plus, Pencil, Trash2 } from 'lucide-react';

interface LineItemsTabProps {
  lineItems: BudgetLineItem[];
  isDelegated: boolean;
  projectId: string;
}

const formatCurrency = (value: number) => {
  return `$ ${value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

export const LineItemsTab: React.FC<LineItemsTabProps> = ({
  lineItems,
  isDelegated,
  projectId,
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingLineItem, setEditingLineItem] = useState<BudgetLineItem | null>(null);
  const [deleteLineItemId, setDeleteLineItemId] = useState<string | null>(null);

  const { mutate: createLineItem } = useCreateLineItem();
  const { mutate: updateLineItem } = useUpdateLineItem();
  const { mutate: deleteLineItem } = useDeleteLineItem();

  const ownLineItems = useMemo(
    () => lineItems.filter((e) => e.isOwnItem),
    [lineItems],
  );
  const subcontractorLineItems = useMemo(
    () => lineItems.filter((e) => !e.isOwnItem),
    [lineItems],
  );

  const handleCreate = (values: {
    description: string;
    quantity: number;
    unitOfMeasure: string;
    amountSystem: string;
    unitPrice: number;
    salePricePerUnit: number | null;
    chargeToClient: boolean;
    discountType: 'USD' | 'PERCENT' | null;
    discountValue: number;
    notes: string;
  }) => {
    createLineItem(
      {
        projectId,
        description: values.description,
        quantity: values.quantity,
        unitOfMeasure: values.unitOfMeasure,
        amountSystem: values.amountSystem,
        unitPrice: values.unitPrice,
        salePricePerUnit: values.salePricePerUnit,
        chargeToClient: values.chargeToClient,
        discountType: values.discountType,
        discountValue: values.discountValue,
        notes: values.notes,
      },
      {
        onSuccess: () => setIsFormOpen(false),
      },
    );
  };

  const handleUpdate = (values: {
    description: string;
    quantity: number;
    unitOfMeasure: string;
    amountSystem: string;
    unitPrice: number;
    salePricePerUnit: number | null;
    chargeToClient: boolean;
    discountType: 'USD' | 'PERCENT' | null;
    discountValue: number;
    notes: string;
  }) => {
    if (!editingLineItem) return;
    updateLineItem(
      {
        lineItemId: editingLineItem.lineItemId,
        description: values.description,
        quantity: values.quantity,
        unitOfMeasure: values.unitOfMeasure,
        amountSystem: values.amountSystem,
        unitPrice: values.unitPrice,
        salePricePerUnit: values.salePricePerUnit,
        chargeToClient: values.chargeToClient,
        discountType: values.discountType,
        discountValue: values.discountValue,
        notes: values.notes,
      },
      {
        onSuccess: () => {
          setEditingLineItem(null);
          setIsFormOpen(false);
        },
      },
    );
  };

  const handleDelete = () => {
    if (!deleteLineItemId) return;
    deleteLineItem(deleteLineItemId, {
      onSuccess: () => setDeleteLineItemId(null),
    });
  };

  const ownColumns = useMemo(() => {
    const cols: any[] = [
      {
        key: 'description',
        label: 'Description',
        className: 'col-span-2',
        render: (v: string) => v || '-',
      },
      {
        key: 'quantity',
        label: 'Qty',
        className: 'col-span-1',
        render: (v: number, row: BudgetLineItem) => `${Number(v) || 0} ${row.unitOfMeasure || ''}`.trim(),
      },
      {
        key: 'unitPrice',
        label: 'Cost per unit',
        className: 'col-span-1',
        render: (v: number) => (v ? formatCurrency(v) : '-'),
      },
      {
        key: 'cost',
        label: 'Total cost',
        className: 'col-span-1',
        render: (v: number) => (v ? formatCurrency(v) : '-'),
      },
      {
        key: 'salePricePerUnit',
        label: 'Sale per unit',
        className: 'col-span-1',
        render: (v: number | null) => (v ? formatCurrency(v) : '-'),
      },
      {
        key: 'salePrice',
        label: 'Total sale',
        className: 'col-span-1',
        render: (v: number | null) => (v ? formatCurrency(v) : '-'),
      },
    ];

    cols.push({
      key: 'actions',
      label: 'Actions',
      className: 'col-span-1',
      render: (_: any, row: BudgetLineItem) => (
        <div className="flex items-center gap-2">
          <button type="button"
            onClick={(e) => {
              e.stopPropagation();
              setEditingLineItem(row);
              setIsFormOpen(true);
            }}
            className="p-1 text-gray-500 hover:text-blue-600 rounded">
            <Pencil size={14} />
          </button>
          <button type="button"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteLineItemId(row.lineItemId);
            }}
            className="p-1 text-gray-500 hover:text-red-600 rounded">
            <Trash2 size={14} />
          </button>
        </div>
      ),
    });

    return cols;
  }, []);

  // Subcontractor table: this org is the delegating company viewing items charged TO it
  // by another org. The backend already replaces the sub's `salePricePerUnit`/`salePrice`
  // into the DTO's `unitPrice`/`cost` fields and nulls the originals, so the sub's real
  // internal cost is never wire-exposed to this org.
  const subColumns = useMemo(
    () => [
      {
        key: 'description',
        label: 'Description',
        className: 'col-span-2',
        render: (v: string) => v || '-',
      },
      {
        key: 'quantity',
        label: 'Qty',
        className: 'col-span-1',
        render: (v: number, row: BudgetLineItem) => `${Number(v) || 0} ${row.unitOfMeasure || ''}`.trim(),
      },
      {
        key: 'unitPrice',
        label: 'Cost per unit',
        className: 'col-span-1',
        render: (v: number) => (v ? formatCurrency(v) : '-'),
      },
      {
        key: 'cost',
        label: 'Total cost',
        className: 'col-span-1',
        render: (v: number) => (v ? formatCurrency(v) : '-'),
      },
      {
        key: 'organizationName',
        label: 'Organization',
        className: 'col-span-1',
        render: (v: string) => v || '-',
      },
    ],
    [],
  );

  const totalOwnCost = ownLineItems.reduce((sum, e) => sum + (e.cost || 0), 0);
  const totalSubCost = subcontractorLineItems.reduce(
    (sum, e) => sum + (e.cost || 0),
    0,
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-gray-700">Your Line Items</p>
        <Button
          variant="tertiary"
          leftIcon={<Plus size={14} />}
          onClick={() => {
            setEditingLineItem(null);
            setIsFormOpen(true);
          }}>
          Add Line Item
        </Button>
      </div>

      <DataTable
        height={subcontractorLineItems.length > 0 ? 250 : 400}
        loading={false}
        columns={ownColumns}
        data={ownLineItems}
        compact
        emptyMessage="No line items added yet"
      />

      {subcontractorLineItems.length > 0 && (
        <>
          <p className="text-sm font-medium text-gray-700 mt-4 mb-2">
            Subcontractor Charges
          </p>
          <DataTable
            height={200}
            loading={false}
            columns={subColumns}
            data={subcontractorLineItems}
            compact
          />
        </>
      )}

      <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-md mt-2 grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-0.5">
          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
            Your Line Items Total
          </p>
          <p className="text-sm font-bold text-gray-900">
            {formatCurrency(totalOwnCost)}
          </p>
        </div>
        {subcontractorLineItems.length > 0 && (
          <div className="flex flex-col gap-0.5">
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
              Subcontractor Charges Total
            </p>
            <p className="text-sm font-bold text-gray-900">
              {formatCurrency(totalSubCost)}
            </p>
          </div>
        )}
      </div>

      <LineItemFormModal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingLineItem(null);
        }}
        lineItem={editingLineItem}
        onSubmit={editingLineItem ? handleUpdate : handleCreate}
      />

      <ConfirmationModal
        title="Delete Line Item"
        description="Are you sure you want to delete this line item?"
        confirmText="Delete"
        isOpen={!!deleteLineItemId}
        onClose={() => setDeleteLineItemId(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};
